import { FacilityEntityType, IEffectiveFacilityPolicy } from "types";

/**
 * Returns localized labels for facility closure entity types.
 * Labels indicate that an entity (office, floor, area, or resource) is closed.
 */
export function getFacilityClosureLabels(): Record<FacilityEntityType, string> {
  return {
    [FacilityEntityType.OFFICE]: String($localize`:@@common|facility-office-closed:Office closed`),
    [FacilityEntityType.FLOOR]: String($localize`:@@common|facility-floor-closed:Floor closed`),
    [FacilityEntityType.ZONE]: String($localize`:@@common|facility-zone-closed:Area closed`),
    [FacilityEntityType.RESOURCE]: String($localize`:@@common|facility-resource-closed:Resource closed`),
  };
}

/** Mapping from closure level strings to FacilityEntityType */
export type FacilityClosureLevel = "office" | "floor" | "area" | "resource";

const CLOSURE_LEVEL_TO_ENTITY_TYPE: Record<FacilityClosureLevel, FacilityEntityType> = {
  office: FacilityEntityType.OFFICE,
  floor: FacilityEntityType.FLOOR,
  area: FacilityEntityType.ZONE,
  resource: FacilityEntityType.RESOURCE,
};

/**
 * Determines the closure level from a facility policy.
 * The level is determined by the most specific identifier in the policy.
 * @param policy - The effective facility policy
 * @returns The closure level or null if policy is null/undefined
 */
export function getClosureLevelFromPolicy(
  policy: IEffectiveFacilityPolicy | null | undefined,
): FacilityClosureLevel | null {
  if (!policy) return null;
  if (policy.resourceUuid) return "resource";
  if (policy.zoneUuid) return "area";
  if (policy.floorUuid) return "floor";
  return "office";
}

/**
 * Returns the localized closure label for a given closure level.
 * @param level - The closure level (office, floor, area, or resource)
 * @returns The localized label string or null if level is null/undefined
 */
export function getClosureLabelForLevel(level: FacilityClosureLevel | null | undefined): string | null {
  if (!level) return null;
  const entityType = CLOSURE_LEVEL_TO_ENTITY_TYPE[level];
  return getFacilityClosureLabels()[entityType];
}

/**
 * Returns the localized closure label for a facility policy.
 * Combines getClosureLevelFromPolicy and getClosureLabelForLevel.
 * @param policy - The effective facility policy
 * @returns The localized label string or null if policy is null/undefined
 */
export function getClosureLabelFromPolicy(policy: IEffectiveFacilityPolicy | null | undefined): string | null {
  return getClosureLabelForLevel(getClosureLevelFromPolicy(policy));
}
